import React, { useEffect, useMemo, useState } from "react";
import { useMindspaceStore, useIsGuest } from "@store";
import { trackEvent } from "../../services/analyticsService";
import { logDebug, logError } from "../../utils/logging";
// Reuse modal form styles for consistency
import styles from "../common/BusinessProfileModal.module.css";
import pageStyles from "./MindspaceInfoPage.module.css";

import { fetchContextKeyDefinitions, ContextKeyDefinition } from "../../services/apiClientV2";

// Dynamic form data
type FormData = Record<string, string>;

const CATEGORY_LABELS: Record<string, string> = {
  identity: "Identitate Brand",
  strategic: "Obiective Strategice",
  context: "Context Piață",
  preferences: "Preferințe",
  altele: "Altele"
};



const MindspaceInfoPage: React.FC = () => {
  const isGuest = useIsGuest();
  const {
    getActiveMindspace,
    activeMindspaceId,
    contextItems,
    getContextValue,
    updateContextItem,
    error: mindspaceError,
    clearError,
  } = useMindspaceStore();

  const activeMindspace = getActiveMindspace();

  const [form, setForm] = useState<FormData>({});
  const [errors, setErrors] = useState<Partial<FormData> & { general?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [keyDefinitions, setKeyDefinitions] = useState<ContextKeyDefinition[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);

  // Calculate if we have any existing data based on loaded keys
  const hasExistingData = useMemo(
    () => {
      if (keyDefinitions.length === 0) return false;
      return contextItems.some((i) =>
        keyDefinitions.some(k => k.key_name === i.item_type)
      );
    },
    [contextItems, keyDefinitions],
  );

  // Group keys for display
  const groupedKeys = useMemo(() => {
    const groups: Record<string, ContextKeyDefinition[]> = {};
    keyDefinitions.forEach(def => {
      const cat = def.category || "altele";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(def);
    });
    return groups;
  }, [keyDefinitions]);

  // Load keys definition
  useEffect(() => {
    let mounted = true;
    const loadKeys = async () => {
      setIsLoadingKeys(true);
      try {
        const defs = await fetchContextKeyDefinitions();
        if (mounted) {
          // Filter persistent keys only and sort
          const persistentDefs = defs
            .filter(d => d.is_persistent && d.is_active)
            .sort((a, b) => a.sort_order - b.sort_order);

          setKeyDefinitions(persistentDefs);

          // Initial form population logic moved to separate effect 
          // to run after keys are loaded AND when context changes
        }
      } catch (err) {
        logError("Failed to load context definitions", err);
        if (mounted) setErrors(prev => ({ ...prev, general: "Nu am putut încărca definițiile contextului." }));
      } finally {
        if (mounted) setIsLoadingKeys(false);
      }
    };
    loadKeys();
    return () => { mounted = false; };
  }, []);

  // Sync form with store when keys or context changes (and not submitting)
  useEffect(() => {
    if (keyDefinitions.length > 0 && activeMindspaceId) {
      clearError();
      const newForm: FormData = {};
      keyDefinitions.forEach(def => {
        newForm[def.key_name] = getContextValue(def.key_name) || "";
      });
      setForm(newForm);

      trackEvent("mindspace_info_viewed", {
        mindspace_id: activeMindspaceId,
        has_existing_data: hasExistingData,
        keys_count: keyDefinitions.length
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyDefinitions, activeMindspaceId, getContextValue]); // contextItems change triggers re-render but getContextValue gets fresh data

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<FormData> = {};

    // Optional validation logic - can be expanded based on key definition metadata
    // For now, mirroring BusinessProfileModal which is permissive, 
    // but preserving specific checks if meaningful or requested.
    // The previous hardcoded checks were:
    // - product/audience: min 10 chars
    // - brand_voice: min 5 chars
    // - others: optional but if present min 3 chars

    // We'll apply loose validation to ensure better data quality
    keyDefinitions.forEach(def => {
      const val = form[def.key_name]?.trim() || "";
      if (val) {
        if (val.length < 3) {
          next[def.key_name] = "Textul este prea scurt (min. 3 caractere)";
        }
      }
      // Note: We could enforce required fields here if we had that metadata
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleReset = () => {
    const newForm: FormData = {};
    keyDefinitions.forEach(def => {
      newForm[def.key_name] = getContextValue(def.key_name) || "";
    });
    setForm(newForm);
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeMindspaceId) return;
    if (isGuest) return; // read-only for guests
    if (!validate()) return;

    setIsSubmitting(true);
    clearError();
    try {
      const updates = Object.entries(form)
        .filter(([, v]) => v.trim() !== "") // Only send non-empty? Or should we allow clearing?
        // Logic: if value is empty string but stored value exists, we should update to clear it?
        // Current backend supports empty string updates.
        // Let's check difference against store to minimize requests.
        .filter(([key, value]) => {
          const current = getContextValue(key) || "";
          return value.trim() !== current.trim();
        })
        .map(([k, v]) => updateContextItem(k, v.trim()));

      if (updates.length > 0) {
        await Promise.all(updates);
        trackEvent("mindspace_info_saved", {
          mindspace_id: activeMindspaceId,
          updated_fields_count: updates.length,
        });
        logDebug("[MindspaceInfoPage] Saved context items", {
          count: updates.length,
        });
      } else {
        logDebug("[MindspaceInfoPage] No changes to save");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logError("[MindspaceInfoPage] Error saving context items", err);
      setErrors((prev) => ({ ...prev, general: msg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={pageStyles.page}>
      <h1 className={pageStyles.title}>Informații Mindspace {activeMindspace?.name && `- ${activeMindspace.name}`}</h1>
      {!activeMindspaceId && (
        <p className={pageStyles.muted}>
          Se încarcă Mindspace‑ul sau nu este disponibil încă. Reîncarcă pagina
          dacă problema persistă.
        </p>
      )}

      {/* Info/CTA for guests */}
      {isGuest && (
        <div className={`${styles.emptyState} ${pageStyles.spacedTop}`}>
          <p>
            Ești în modul oaspete. Autentifică‑te pentru a salva profilul de
            afacere și a beneficia de răspunsuri mai relevante.
          </p>
        </div>
      )}

      {/* General error */}
      {(errors.general || mindspaceError) && (
        <div className={`${styles.generalError} ${pageStyles.spacedBottom}`}>
          {errors.general || mindspaceError}
        </div>
      )}

      {isLoadingKeys ? (
        <div className={pageStyles.loadingState}>
          <p>Se încarcă câmpurile...</p>
        </div>
      ) : keyDefinitions.length === 0 ? (
        <div className={pageStyles.emptyState}>
          <p>Nu există definiții pentru profil.</p>
        </div>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit}>

          {Object.entries(groupedKeys).map(([category, defs]) => (
            <div key={category} className={pageStyles.section}>
              <h3 className={pageStyles.categoryHeader}>
                {CATEGORY_LABELS[category] || category}
              </h3>

              {defs.map((def) => (
                <div className={styles.fieldGroup} key={def.key_name}>
                  <label className={styles.label} htmlFor={def.key_name}>
                    {def.display_label}
                  </label>

                  {/* Heuristic for input type: short fields use input, long use textarea */}
                  {/* For now, using textarea for flexibility as most are descriptions */}
                  {def.key_name === "brand_voice" || def.key_name === "business_name" ? (
                    <input
                      id={def.key_name}
                      type="text"
                      className={`${styles.input} ${errors[def.key_name] ? styles.error : ""}`}
                      value={form[def.key_name] || ""}
                      onChange={(e) => handleChange(def.key_name, e.target.value)}
                      disabled={isSubmitting || isGuest}
                      maxLength={100}
                      placeholder={def.description}
                    />
                  ) : (
                    <textarea
                      id={def.key_name}
                      className={`${styles.textarea} ${errors[def.key_name] ? styles.error : ""}`}
                      rows={3}
                      value={form[def.key_name] || ""}
                      onChange={(e) => handleChange(def.key_name, e.target.value)}
                      disabled={isSubmitting || isGuest}
                      maxLength={500}
                      placeholder={def.description}
                    />
                  )}

                  {errors[def.key_name] && (
                    <span className={styles.errorText}>{errors[def.key_name]}</span>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Actions */}
          <div className={styles.actions}>
            <button
              type="button"
              onClick={handleReset}
              className={styles.cancelButton}
              disabled={isSubmitting}
            >
              Resetează
            </button>
            {!isGuest && (
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Se salvează..." : "Salvează"}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default MindspaceInfoPage;
